import { Typography, List, styled } from '@mui/material';
import { CustomPalette } from 'business/definitions';
import { trackEvent } from 'helpers/trackerHelpers';
import React from 'react';
import { LegendDimension } from 'routes/Explore/components/Contexts/LegendDimensionContext/LegendDimensionContext';
import { PaletteName } from 'styles/theme';
import Flex from 'ui/Flex/Flex';
import ColorMapperPicker from './ColorMapperPicker';
import { getCurrentColorMapping } from './helpers/getColors';

type ColorMapperProps = {
    currentPalette: CustomPalette | undefined;
    legendDimension: LegendDimension | undefined;
    defaultPaletteName: PaletteName;
    onChange: (customPalette: CustomPalette) => void;
};

const StyColorMapperBody = styled(Flex, {
    name: 'ColorMapperBody',
})(() => ({
    border: '1px solid lightgrey',
    borderRadius: '4px',
    flexDirection: 'column',
    overflow: 'auto',
    maxHeight: '400px',
}));

const ColorMapper = ({
    currentPalette,
    legendDimension,
    defaultPaletteName,
    onChange,
}: ColorMapperProps) => {
    const { key: dimensionKey, dimensionValues } = legendDimension || {};
    const currentColorMapping = getCurrentColorMapping(
        currentPalette,
        defaultPaletteName,
        dimensionKey,
    );

    const onColorChange = (
        dimensionValue: string,
        color: string | undefined,
    ) => {
        if (!dimensionKey) {
            return;
        }
        const mappingsForDefaultPalette = currentPalette
            ? currentPalette[defaultPaletteName]
            : {};
        const definedCurrentPalette = currentPalette ?? {};

        // If the color is undefined, we want to unset the color set for this dimension.
        // Otherwise, we just set it as intended.
        let newColorMapping;
        if (color !== undefined) {
            newColorMapping = [
                {
                    value: dimensionValue,
                    color,
                },
                ...currentColorMapping.filter(
                    (v) => v.value !== dimensionValue,
                ),
            ];
        } else {
            newColorMapping = currentColorMapping.filter(
                (v) => v.value !== dimensionValue,
            );
        }

        const paletteToSave: CustomPalette = {
            ...definedCurrentPalette,
            [defaultPaletteName]: {
                ...mappingsForDefaultPalette,
                [dimensionKey]: newColorMapping,
            },
        };

        onChange(paletteToSave);
        trackEvent('explore/config/change-custom-color');
    };

    return (
        <Flex sx={{ marginTop: 1 }} direction="column">
            <Typography variant="caption" sx={{ color: '#606060' }}>
                Modifier les couleurs
            </Typography>
            <StyColorMapperBody>
                <List>
                    {dimensionValues &&
                        dimensionValues.map((dimensionValue) => {
                            const colorMapping = currentColorMapping.find(
                                (mapping) => mapping.value === dimensionValue,
                            ) || { color: undefined };
                            return (
                                <ColorMapperPicker
                                    key={dimensionValue}
                                    color={colorMapping.color}
                                    dimensionValue={dimensionValue}
                                    defaultPaletteName={defaultPaletteName}
                                    onChange={onColorChange}
                                />
                            );
                        })}
                </List>
            </StyColorMapperBody>
        </Flex>
    );
};

export default React.memo(ColorMapper);
